import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/shallow';
import { ROUTES } from '@/constants';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { useAuthStore } from '@/store/useAuthStore';
import {
  hasRequiredRoutesPermissions,
  hasRequiredRoutesRoles,
} from '@/utils/authUtils';

export function withPrivateRoute(
  Component,
  {
    requiredRoles = [],
    requiredPermissions = [],
    bypassAuthCheck = false,
    requiredCompanyFlag = null,
  } = {}
) {
  function PrivateEnhancedComponent(props) {
    const navigate = useNavigate();

    const { user, setUserData } = useAuthStore(
      useShallow((state) => ({
        user: state.user,
        setUserData: state.setUserData,
      }))
    );

    useEffect(() => {
      if (!user) {
        navigate(ROUTES.login, { replace: true });
      }
    }, [navigate, user]);

    const {
      data: userData,
      isFetched,
      isStale,
    } = useQuery({
      queryKey: [QUERY_KEY.userCheck, user?.userId],
      queryFn: async () => await API.auth.fetchUserCheckStatus(),
      refetchInterval: 1 * 60 * 1000,
      staleTime: 1 * 60 * 1000,
      enabled: !!user,
      retry: false,
    });

    useEffect(() => {
      // TODO: debugging purpose
      // console.debug('🚀 ~ PrivateEnhancedComponent ~ userData:', userData);

      if (isFetched && isStale && userData) {
        setUserData({ ...userData });
      }
    }, [userData, isStale, isFetched, setUserData]);

    const checkAccess = useMemo(() => {
      const userRoles = user?.roles || [];
      const userPermissions = user?.permissions || [];
      const hasRoles = hasRequiredRoutesRoles(userRoles, requiredRoles);

      const hasPermissions = hasRequiredRoutesPermissions(
        userPermissions,
        requiredPermissions
      );

      return hasRoles && hasPermissions;
    }, [user]);

    if (!user) {
      return <Navigate to={ROUTES.login} />;
    }

    if (!bypassAuthCheck && !checkAccess) {
      return <Navigate to={ROUTES.accessDenied} replace />;
    }

    // Per-company module gate (e.g. hasTools/hasCalculator from login payload);
    // undefined (stale persisted store) must NOT block access
    if (requiredCompanyFlag && user?.[requiredCompanyFlag] === false) {
      return <Navigate to={ROUTES.accessDenied} replace />;
    }

    return <Component {...props} />;
  }

  PrivateEnhancedComponent.displayName = `PrivateEnhancedComponent(${Component.displayName || Component.name || 'Component'})`;

  return PrivateEnhancedComponent;
}

export default withPrivateRoute;
